import React, { useState, useEffect, useCallback, useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { CurrencyContext } from '../currencyContext'
import {
    Container, Box, Typography, Grid, Link, Chip,
    useMediaQuery, useTheme, Modal, IconButton, Tooltip, Snackbar
} from '@mui/material'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ShareIcon from '@mui/icons-material/Share'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import InquireProperty from '../components/inquireProperty'
import BedIcon from '@mui/icons-material/Bed'
import BathroomIcon from '@mui/icons-material/Bathroom'
import ArchitectureIcon from '@mui/icons-material/Architecture'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LoadingComponent from '../components/loadingComponent'
import { getProperty } from '../backend/api'
import { PROPERTIES } from '../routes'
import { LandSizeUnitContext } from '../landSizeUnitContext'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
})

const BASE_URL    = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const buildImageUrl = (path) => {
    if (!path) return null
    if (path.startsWith('http')) return path.replace('https//', 'https://')
    return `${BASE_URL}${path}`
}

export default function PropertyDetailScreen() {
    const params = useParams()
    const slug   = params.propertyID

    const theme    = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const currency     = useContext(CurrencyContext).value
    const landSizeUnit = useContext(LandSizeUnitContext).value

    const [loading,  toggleLoading]  = useState(true)
    const [error,    toggleError]    = useState(false)
    const [property, setProperty]    = useState(null)
    const [copied,   setCopied]      = useState(false)

    const [lightboxOpen,  setLightboxOpen]  = useState(false)
    const [lightboxIndex, setLightboxIndex] = useState(0)

    const galleryImages = property?.gallery_images || []
    const mainImageObj  = property?.main_image ? [{ id: 0, image: buildImageUrl(property.main_image) }] : []
    const images = [
        ...mainImageObj,
        ...galleryImages.map(g => ({ id: g.id, image: buildImageUrl(g.image), caption: g.caption }))
    ]

    const price         = property ? parseFloat(property.display_price) * currency.value : 0
    const isDiscounted  = property?.discounted_price && property.discounted_price !== property.price
    const originalPrice = isDiscounted ? parseFloat(property.price) * currency.value : null

    const areaSqft    = property?.area_sqft ? parseFloat(property.area_sqft) : null
    const sizeDisplay = areaSqft ? (areaSqft * landSizeUnit.value).toFixed(2) : null

    const rooms = property?.room_count || {}
    const beds  = rooms.bedrooms  ?? null
    const baths = rooms.bathrooms ?? null

    const amenities           = property?.amenities || []
    const title               = property?.title || ''
    const description         = property?.description || ''
    const location            = property?.location || ''
    const parish              = property?.parish_display || ''
    const status              = property?.status_display || ''
    const categoryDisplay     = property?.category_display || ''
    const propertyTypeDisplay = property?.property_type_display || ''
    const isSold              = property?.status === 'sold'

    // ── Share URL — points to Django view, not the hash URL ──
    const shareUrl = `https://www.choicerealestateda.com/#/properties/${slug}`

    function handleShare() {
        if (navigator.share) {
            navigator.share({ title, url: shareUrl })
        } else {
            navigator.clipboard.writeText(shareUrl)
            setCopied(true)
        }
    }

    const getData = useCallback(async () => {
        try {
            toggleLoading(true)
            const propData = await getProperty(slug)
            setProperty(propData)
            toggleError(false)
        } catch (e) {
            toggleError(true)
        } finally {
            toggleLoading(false)
        }
    }, [slug])

    useEffect(() => { getData() }, [getData])

    function openLightbox(index) { setLightboxIndex(index); setLightboxOpen(true) }
    function lightboxPrev(e) { e.stopPropagation(); setLightboxIndex(i => (i <= 0 ? images.length - 1 : i - 1)) }
    function lightboxNext(e) { e.stopPropagation(); setLightboxIndex(i => (i >= images.length - 1 ? 0 : i + 1)) }

    if (loading) return <LoadingComponent />
    if (error) return (
        <Container disableGutters maxWidth='xl' sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, justifyContent: 'center', height: '100vh' }}>
            <SearchOffIcon fontSize='large' />
            <Typography variant='body1'>Property does not exist</Typography>
            <Link component={RouterLink} to='/'><Typography>Go Home</Typography></Link>
        </Container>
    )

    const metaTitle = title ? `${title} | Choice Real Estate` : 'Choice Real Estate'
    const metaDescription = property?.meta_description || description.substring(0, 155) || 'Browse properties in Dominica with Choice Real Estate'
    const metaImage = images.length > 0 ? images[0].image : `${BASE_URL}/logo.jpeg`
    const canonicalUrl = `${BASE_URL}/share/property/${slug}/`

    return (
        <Container disableGutters maxWidth='xl' sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                
                {/* Open Graph / Facebook / WhatsApp / LinkedIn */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={metaImage} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:site_name" content="Choice Real Estate" />
                
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={metaImage} />
                
                {/* Canonical URL */}
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>

            <Grid container spacing={2} alignItems='flex-start' sx={{ width: '100%', px: { xs: 1, md: 2 } }}>

                {/* ── Left: image grid + price summary ── */}
                <Grid item xs={12} md={7}>

                    {images.length > 0 && (
                        <Box
                            onClick={() => openLightbox(0)}
                            sx={{
                                cursor: 'zoom-in',
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 1,
                                height: isMobile ? 240 : 420,
                                bgcolor: '#E8ECF0',
                            }}
                        >
                            <img
                                src={images[0].image}
                                alt={title}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        </Box>
                    )}

                    {images.length > 1 && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            {images.slice(1).map((img, i) => (
                                <Box
                                    key={img.id}
                                    onClick={() => openLightbox(i + 1)}
                                    sx={{
                                        width: 72, height: 56,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        cursor: 'zoom-in',
                                        border: '2px solid transparent',
                                        flexShrink: 0,
                                        '&:hover': { borderColor: 'primary.main' },
                                        transition: 'border-color 0.15s',
                                    }}
                                >
                                    <img src={img.image} alt={img.caption || `Photo ${i + 2}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* ── Price / status summary bar ── */}
                    <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 2, mt: 1 }}>
                        <Typography variant='overline' color='text.dimGrey'>
                            {categoryDisplay}{propertyTypeDisplay ? ` — ${propertyTypeDisplay}` : ''}
                        </Typography>
                        <Typography variant='h4' sx={{ fontWeight: 700, color: isSold ? 'error.main' : 'text.primaryGreen', mt: 0.25 }}>
                            {isSold && <span style={{ marginRight: 8 }}>SOLD —</span>}
                            {price.toLocaleString('en-US', { style: 'currency', currency: currency.name })}
                        </Typography>
                        {isDiscounted && (
                            <Typography variant='body2' sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                {originalPrice.toLocaleString('en-US', { style: 'currency', currency: currency.name })}
                            </Typography>
                        )}

                        {/* Status row + share button */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                            <LocationOnIcon fontSize='small' color='action' />
                            <Typography variant='body2' color='text.secondary'>
                                {[location, parish].filter(Boolean).join(', ')}
                            </Typography>
                            <Chip label={status} color={isSold ? 'error' : 'success'} size='small' />
                            <Box sx={{ flexGrow: 1 }} />
                            <Tooltip title='Share this property'>
                                <IconButton onClick={handleShare} size='small' color='primary'>
                                    <ShareIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Grid>

                {/* ── Right: enquiry form ── */}
                <Grid item xs={12} md={5}>
                    <InquireProperty propertyID={property?.id} slug={slug} />
                </Grid>
            </Grid>

            {/* ── Description & Details ── */}
            <Box sx={{ bgcolor: 'background.paper', p: { xs: 2, md: 4 }, width: '100%', mt: 2 }}>
                <Typography variant='h4' sx={{ mb: 2 }}>{title}</Typography>

                <Typography variant='h6' sx={{ mb: 1 }}>Overview</Typography>
                <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 2, mb: 3 }}>
                    <Typography 
                        variant='body1' 
                        sx={{ 
                            textAlign: 'justify', 
                            lineHeight: 1.8,
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                        }}
                    >
                        {description}
                    </Typography>
                </Box>

                <Typography variant='h6' sx={{ mb: 1 }}>Details</Typography>
                <Grid container spacing={2} sx={{ bgcolor: 'background.default', borderRadius: 2, p: 1, mb: 2 }}>
                    {beds != null && (
                        <Grid item xs={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <BedIcon color='action' fontSize='small' />
                                <Typography variant='body2'>Bedrooms: {beds}</Typography>
                            </Box>
                        </Grid>
                    )}
                    {baths != null && (
                        <Grid item xs={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <BathroomIcon color='action' fontSize='small' />
                                <Typography variant='body2'>Bathrooms: {baths}</Typography>
                            </Box>
                        </Grid>
                    )}
                    {sizeDisplay && (
                        <Grid item xs={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ArchitectureIcon color='action' fontSize='small' />
                                <Typography variant='body2'>Size: {sizeDisplay} {landSizeUnit.name}</Typography>
                            </Box>
                        </Grid>
                    )}
                    {location && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOnIcon color='action' fontSize='small' />
                                <Typography variant='body2'>{[location, parish].filter(Boolean).join(', ')}</Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {amenities.length > 0 && (
                    <>
                        <Typography variant='h6' sx={{ mt: 2, mb: 1 }}>Amenities</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {amenities.map((a, i) => (
                                <Chip key={i} icon={<CheckCircleIcon />} label={a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} variant='outlined' color='success' />
                            ))}
                        </Box>
                    </>
                )}
            </Box>

            {/* ── Map ── */}
            {property?.latitude && property?.longitude && (
                <Box sx={{ width: '100%', mt: 3, mb: 1, px: { xs: 1, md: 2 } }}>
                    <Typography variant='h6' textAlign='center' sx={{ mb: 1 }}>Location</Typography>
                    <MapContainer
                        center={[property.latitude, property.longitude]}
                        zoom={15}
                        style={{ height: 350, width: '100%', borderRadius: 8 }}
                        scrollWheelZoom={false}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                        />
                        <Marker position={[property.latitude, property.longitude]}>
                            <Popup>{title || location}</Popup>
                        </Marker>
                    </MapContainer>
                </Box>
            )}

            <Link component={RouterLink} to={`../${PROPERTIES}`}>
                <Typography sx={{ my: 2 }}>View more listings</Typography>
            </Link>

            {/* ── Lightbox Modal ── */}
            <Modal open={lightboxOpen} onClose={() => setLightboxOpen(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box
                    sx={{ position: 'relative', outline: 'none', maxWidth: '92vw', maxHeight: '92vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setLightboxOpen(false)}
                >
                    <img
                        src={images[lightboxIndex]?.image}
                        alt={images[lightboxIndex]?.caption || title}
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '90vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 8, display: 'block', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
                    />
                    <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', top: -16, right: -16, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' } }}>
                        <CloseIcon />
                    </IconButton>
                    {images.length > 1 && (
                        <IconButton onClick={lightboxPrev} sx={{ position: 'absolute', left: -20, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                            <ChevronLeftIcon />
                        </IconButton>
                    )}
                    {images.length > 1 && (
                        <IconButton onClick={lightboxNext} sx={{ position: 'absolute', right: -20, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                            <ChevronRightIcon />
                        </IconButton>
                    )}
                    {(images[lightboxIndex]?.caption || images.length > 1) && (
                        <Box sx={{ position: 'absolute', bottom: -32, left: 0, right: 0, textAlign: 'center' }}>
                            <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                {images[lightboxIndex]?.caption && `${images[lightboxIndex].caption} · `}
                                {lightboxIndex + 1} / {images.length}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* ── Clipboard snackbar ── */}
            <Snackbar
                open={copied}
                autoHideDuration={2500}
                onClose={() => setCopied(false)}
                message='Link copied to clipboard'
            />

        </Container>
    )
}